import { useMemo } from "react";
import { useAuth } from "@/lib/auth-context";
import { useCotacaoHoje, usePracaDaOrg } from "@/hooks/use-cotacoes";
import { useVariedades } from "@/hooks/use-variedades-cacho";
import { chaveProduto, produtoDoItem } from "@shared/mercado-produtos";
import { fmtMoedaBR } from "@/lib/formato-mercado";
import { labelReferencial } from "@shared/schema";
import type { ProdutoVenda } from "@shared/schema";
import { dataCurtaBR } from "@shared/boletim-periodo";
import { diaCivilNaFazenda } from "@shared/data-fazenda";

/**
 * A ressalva que acompanha cada referencial — `null` quando não há o que
 * ressalvar.
 *
 * ⚠️ Com placa de VENDA, o número do card NÃO é o que o produtor recebe: é o
 * preço da caixa embalada, depois do packing, do frete e da margem de quem
 * revende. Sem a ressalva, o produtor digita 1,60 no romaneio porque foi o que
 * a praça "disse" — e o card vira uma sugestão de preço que ninguém quis dar.
 *
 * ⚠️ "pode variar", nunca "é menor". A plataforma registra o que a associação
 * divulgou e não arbitra a distância entre a placa e o portão da fazenda —
 * mesma razão pela qual a comparação com placa de venda sai em R$/kg e nunca
 * como "vendeu abaixo da praça" (059).
 *
 * Com placa AO PRODUTOR não há ressalva: o número já é o que ele recebe, e uma
 * linha a mais só ensinaria o produtor a ignorar a linha.
 */
export function ressalvaDoReferencial(referencial: string | null | undefined): string | null {
  return referencial === "VENDA" ? "O preço ao produtor pode variar." : null;
}

/**
 * A DATA que a praça deve mostrar para uma venda de `dataVenda` — `undefined` =
 * hoje (o servidor corta em hoje de qualquer jeito, e sem `ate` a consulta é a
 * mesma do quadro de Cotações, com o mesmo cache).
 *
 * ⚠️ Venda de 13/09 editada em 25/09 mostra a praça de 13/09 (Hugo, 25/09): o
 * preço de hoje ao lado de um preço fechado semanas atrás vira sugestão errada
 * no pré-preenchimento e acusação na leitura — a mesma regra da página pública
 * do romaneio (`LinhaPracaDaVenda`).
 */
export function ateDaVenda(dataVenda: string | null | undefined, hoje: string): string | undefined {
  if (!dataVenda || !/^\d{4}-\d{2}-\d{2}$/.test(dataVenda)) return undefined;
  return dataVenda < hoje ? dataVenda : undefined;
}

/**
 * O título do card: "Praça Mirorós hoje · ao produtor" ou, numa venda de outro
 * dia, "Praça Mirorós em 13/09 · ao produtor". `publicada` avisa quando a
 * cotação mostrada é de um dia ANTERIOR ao pedido (a praça não publicou naquele
 * dia e vale a última publicação) — sem isso "em 13/09" afirmaria uma cotação
 * de 13/09 que não existe.
 */
export function tituloDaPraca(d: {
  pracaNome: string;
  referencial: string;
  /** A data pedida (`ateDaVenda`) — `undefined` = hoje. */
  ate: string | undefined;
  /** `CotacaoHojeDTO.data`: o dia da publicação mostrada. */
  dataCotacao: string | null;
  hoje: string;
}): { titulo: string; publicada: string | null } {
  const quando = d.ate ? `em ${dataCurtaBR(d.ate)}` : "hoje";
  const pedido = d.ate ?? d.hoje;
  return {
    titulo: `Praça ${d.pracaNome} ${quando} · ${labelReferencial(d.referencial)}`,
    publicada: d.dataCotacao && d.dataCotacao < pedido ? `cotação publicada em ${dataCurtaBR(d.dataCotacao)}` : null,
  };
}

/**
 * "Praça hoje: 1ª R$ 1,45 · 2ª 0,85 · 3ª 0,30 · Nanica 1,10" — a referência ao
 * lado dos preços, no formulário de venda do Cacho.
 *
 * ⚠️ É A PRAÇA DA DATA DA VENDA (`dataVenda`), não a de hoje: a última
 * publicação até aquele dia. Venda nova (data de hoje) continua vendo "hoje".
 *
 * ⚠️ TOQUE MÍNIMO no formulário: este é um componente montado AO LADO, não uma
 * refatoração do form. O formulário de venda é a tela mais usada do Cacho e a que
 * mais custa quebrar.
 *
 * ⚠️ Sem cotação publicada, o card **não aparece** (devolve `null`). Um card
 * dizendo "sem cotação" no meio do formulário seria ruído numa tela em que o
 * usuário está fazendo outra coisa.
 *
 * ⚠️ A ETIQUETA DO REFERENCIAL É OBRIGATÓRIA, e é derivada da cotação — nunca
 * um texto fixo. O mesmo card mostra placa ao produtor e placa de venda, e são
 * números de naturezas diferentes; qual deles está ali é o que decide se 1,60 é
 * um preço plausível para o romaneio ou o dobro dele.
 */
export function CardPracaHoje({ dataVenda }: { dataVenda?: string | null } = {}) {
  const hoje = diaCivilNaFazenda(new Date());
  const ate = ateDaVenda(dataVenda, hoje);
  const { data: cotacao } = useCotacaoDaPracaDaOrg(ate);
  if (!cotacao || cotacao.linhas.length === 0) return null;
  const ressalva = ressalvaDoReferencial(cotacao.referencial);
  const { titulo, publicada } = tituloDaPraca({
    pracaNome: cotacao.pracaNome, referencial: cotacao.referencial, ate, dataCotacao: cotacao.data, hoje,
  });
  return (
    <div className="rounded-xl border border-dashed bg-muted/40 px-3 py-2" data-testid="card-praca-hoje">
      {/* A etiqueta fica no cabeçalho, e não sob cada preço como no quadro
          grande: aqui os preços cabem numa linha só, imediatamente abaixo — a
          distância que fazia a etiqueta perder o vínculo com os números lá não
          existe neste card. */}
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground" data-testid="texto-praca-referencial">
        {titulo}
        {publicada && <span className="normal-case tracking-normal" data-testid="texto-praca-publicada"> · {publicada}</span>}
      </p>
      <p className="text-sm mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
        {cotacao.linhas.map(l => (
          <span key={`${l.variedade}-${l.qualidade ?? ""}`} className="whitespace-nowrap">
            <span className="text-muted-foreground">{rotuloCurto(l.label)}</span>{" "}
            <b className="font-mono tabular-nums">{fmtMoedaBR(l.precoKg)}</b>
          </span>
        ))}
      </p>
      {ressalva && (
        <p className="text-[11px] text-muted-foreground mt-1" data-testid="texto-praca-ressalva">
          {ressalva}
        </p>
      )}
    </div>
  );
}

/**
 * A cotação da praça DA ORG — de hoje, ou a vigente em `ate` (a data da venda).
 *
 * Duas queries porque são dois assuntos: quem sabe a praça da organização é
 * `minhas-vs-praca` (sessão), e o preço vem da rota pública. Só dispara para org
 * PRODUTORA com o módulo Mercado — sem esse gate, toda venda de uma org sem o
 * módulo geraria um 403 no console a cada abertura do formulário.
 *
 * ⚠️ `ate` SÓ JUNTO COM A PRAÇA. `useCotacaoHoje` roda com qualquer um dos dois,
 * e `ate` sozinho faria o servidor cair na primeira praça ativa — a org sem
 * Mercado (e a que ainda está carregando a praça) passaria a buscar cotação.
 */
function useCotacaoDaPracaDaOrg(ate?: string) {
  const { user, modulesEnabled } = useAuth();
  const ativo = user?.orgTipo !== "COMPRADOR" && modulesEnabled.indexOf("mercado") >= 0;
  const { praca } = usePracaDaOrg();
  const alvo = ativo ? praca : undefined;
  return useCotacaoHoje(alvo, alvo ? ate : undefined);
}

/** "Prata 1ª" → "1ª"; "Nanica" fica "Nanica". A linha tem de caber em 390px. */
function rotuloCurto(label: string): string {
  return label.indexOf("Prata ") === 0 ? label.substring(6) : label;
}

/**
 * `produtoVendaId` → preço da praça NA DATA DA VENDA (hoje, na venda nova),
 * quando existe. É o ponto de partida do R$/kg de um produto adicionado: numa
 * venda de 13/09, o preço de 25/09 seria uma sugestão de outro dia.
 *
 * A ponte é `produtoDoItem` (shared) — a MESMA função que o servidor usa para
 * agregar "minhas vendas × praça". Duas comparações no olho, uma aqui e outra
 * lá, divergiriam no primeiro produto novo do cadastro: a tela pré-preencheria
 * um preço que a agregação depois ignora.
 */
export function usePrecoDaPraca(
  produtos: ProdutoVenda[],
  dataVenda?: string | null,
): (produtoVendaId: string) => number | null {
  const { data: cotacao } = useCotacaoDaPracaDaOrg(ateDaVenda(dataVenda, diaCivilNaFazenda(new Date())));
  const { data: variedades = [] } = useVariedades();

  return useMemo(() => {
    const precos = new Map((cotacao?.linhas ?? []).map(l => [`${l.variedade}|${l.qualidade ?? ""}`, l.precoKg]));
    const nomeDaVariedade = new Map(variedades.map(v => [v.id, v.nome]));
    const porProdutoVenda = new Map<string, number>();
    for (const p of produtos) {
      const produto = produtoDoItem({
        variedadeNome: p.variedadeId ? nomeDaVariedade.get(p.variedadeId) ?? null : null,
        classificacao: p.classificacao,
        produtoNome: p.nome,
      });
      if (!produto) continue;
      const preco = precos.get(chaveProduto(produto));
      if (preco != null) porProdutoVenda.set(p.id, preco);
    }
    return (produtoVendaId: string) => porProdutoVenda.get(produtoVendaId) ?? null;
  }, [cotacao, produtos, variedades]);
}
